import React, { useEffect, useMemo, useState } from 'react';
import { TransactionsNew } from '../../pages';
import { theYear } from '../../utils/utils';
import { TransactionsController } from '../../controllers';
import moment from 'moment';
import { object, string } from 'yup';
import { transactionStore } from '../../stores';
import constant from '../../utils/constant';

const TransactionsNewContainer = () => {
	const [loading, setLoading] = useState(false);
	const [filterPeriod, setFilterPeriod] = useState(`${theYear}`);

	const { transactionSummary, expenseAnalysis } = TransactionsController;

	useEffect(() => {
		const setTransactions = async () => {
			setLoading(true);
			await Promise.all([transactionSummary(), expenseAnalysis()]);
			setLoading(false);
		};
		setTransactions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const validationSchema = () => {
		return object({
			startDate: string().required('Start Date is required'),
			endDate: string().required('End Date is required'),
		});
	};

	//modals

	const refresh = async () => {
		setLoading(true);
		await Promise.all([
			transactionSummary(),
			expenseAnalysis(),
			// CustomerController.getCustomers(),
		]);
		setLoading(false);
	};

	//for tables
	const tableTypeConstant = {
		INFLOW: 'INFLOW',
		OUTFLOW: 'OUTFLOW',
		DEBT: 'DEBT',
	};

	const inflowTagData = [
		{
			key: constant.FLOW_TYPE.All,
			value: 'All Inflow Transactions',
		},
		{
			key: constant.FLOW_TYPE.Income,
			value: 'Income',
		},
		{
			key: constant.FLOW_TYPE.Untagged,
			value: 'Untagged',
		},
		{
			key: constant.FLOW_TYPE.Refund,
			value: 'Refund',
		},
		{
			key: constant.FLOW_TYPE.Funding,
			value: 'Wallet Funding',
		},
		{
			key: constant.FLOW_TYPE.Loan,
			value: 'Loan',
		},
		{
			key: constant.FLOW_TYPE.Investment,
			value: 'Investment',
		},
		{
			key: constant.FLOW_TYPE.Gifts,
			value: 'Gifts And Donations',
		},
		{
			key: constant.FLOW_TYPE.Assets,
			value: 'Sales Of Business Properties',
		},
	];

	const outflowTagData = [
		{
			key: constant.FLOW_TYPE.All,
			value: 'All Outflow Transactions',
		},
		{
			key: constant.FLOW_TYPE.Withdraw,
			value: 'Withdrawal',
		},
		{
			key: constant.FLOW_TYPE.Expense,
			value: 'Expense',
		},
	];

	const [tableType, setTableType] = useState(tableTypeConstant.INFLOW);
	const [showContent, setShowContent] = useState(false);
	const [tagTitle, setTagTitle] = useState(inflowTagData[0].value);
	const [tagKey, setTagKey] = useState(inflowTagData[0].key);

	const tableTypeData = [
		tableTypeConstant.INFLOW,
		tableTypeConstant.OUTFLOW,
		tableTypeConstant.DEBT,
	];

	const tableTagData = useMemo(() => {
		if (tableType === tableTypeConstant.INFLOW) {
			return inflowTagData;
		} else if (tableType === tableTypeConstant.OUTFLOW) {
			return outflowTagData;
		} else {
			return inflowTagData;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tableType]);

	const tableTypeNavHandler = type => {
		setTableType(type);
		if (type === tableTypeConstant.INFLOW) {
			setTagTitle(inflowTagData[0].value);
			setTagKey(inflowTagData[0].key);
		} else if (type === tableTypeConstant.OUTFLOW) {
			setTagTitle(outflowTagData[0].value);
			setTagKey(outflowTagData[0].key);
		} else if (type === tableTypeConstant.DEBT) {
			setTagTitle('');
			setTagKey('');
		}
	};

	const tagContentHandler = () => {
		setShowContent(!showContent);
	};

	const tagSetHandler = item => {
		setTagTitle(item.value);
		setTagKey(item.key);
		tagContentHandler();
	};

	const [dateFilterObject, setDateFilterObject] = useState({});

	const query = useMemo(() => {
		const query = {
			type: tableType,
			...(tagKey && tagKey !== constant.FLOW_TYPE.All
				? { inflowType: tagKey }
				: {}),
			...dateFilterObject,
		};

		return query;
	}, [tagKey, tableType, dateFilterObject]);

	const { useGetTransactions } = TransactionsController;

	const {
		isLoading,
		isFetching,
		currentPage,
		setCurrentPage,
		setSearchQuery,
		limit,
		setLimit,
		data,
	} = useGetTransactions(query);

	const onTransactionLimitChange = limit => {
		setLimit(limit);
		setCurrentPage(0);
	};

	//MODALS
	const modalConstant = {
		close: 'CLOSE',
		addExpense: 'ADD-EXPENSE',
		addIncome: 'ADD-INCOME',
		addDebt: 'ADD-DEBT',
		updateDebt: 'UPDATE-DEBT',
		updateTransaction: 'UPDATE-TRANSACTION',
		editExpense: 'EDIT-EXPENSE',
		editIncome: 'EDIT-INCOME',
		viewIncome: 'VIEW-INCOME',
		viewExpense: 'VIEW-EXPENSE',
		tagTransaction: 'TAG-TRANSACTION',
	};

	const [modalType, setModalType] = useState(modalConstant.close);

	const openModalHandler = item => {
		setModalType(item);
	};

	const closeModal = () => {
		setModalType('CLOSE');
	};

	const handleExpenseOptionSelect = (_id, transaction, option) => {
		transactionStore.setSelectedTransaction(transaction);
		if (option.value === 'View More') {
			// setViewMoreExpense(true);
			setModalType(modalConstant.viewExpense);
		} else if (option.value === 'Update Expense') {
			// setUpdateTransaction(true);
			setModalType(modalConstant.updateTransaction);
		}
	};

	const onSubmit = async payload => {
		let query;

		if (payload && Object.keys(payload).length > 0) {
			const dateFilter = {
				startDate: payload.startDate,
				endDate: payload.endDate,
				dateField: 'createdAt',
			};

			setDateFilterObject(dateFilter);

			if (payload.period === 'custom') {
				setFilterPeriod(
					`${moment(payload?.startDate).format('DD-MM-YYYY')} - ${moment(payload.endDate).format('DD-MM-YYYY')}`
				);
			} else {
				setFilterPeriod(`${payload?.periodText}`);
			}
		}
		setLoading(true);
		await Promise.all([TransactionsController.transactionSummary(query)]);

		setLoading(false);
	};

	const resetFilter = () => {
		onSubmit();
		setFilterPeriod(`${theYear}`);
		setDateFilterObject({});
	};

	return (
		<TransactionsNew
			loading={loading}
			filterPeriod={filterPeriod}
			validationSchema={validationSchema}
			onSubmit={onSubmit}
			resetFilter={resetFilter}
			tableType={tableType}
			refresh={refresh}
			tableTypeNavHandler={tableTypeNavHandler}
			tableTypeData={tableTypeData}
			tagContentHandler={tagContentHandler}
			tagTitle={tagTitle}
			showContent={showContent}
			tableTagData={tableTagData}
			tagSetHandler={tagSetHandler}
			tableTypeConstant={tableTypeConstant}
			transactionDataInfo={data}
			onTransactionLimitChange={onTransactionLimitChange}
			transactionLimit={limit}
			transactionCurrentPage={currentPage}
			setTransactionCurrentPage={setCurrentPage}
			setTransactionSearchQuery={setSearchQuery}
			modalType={modalType}
			modalConstant={modalConstant}
			openModalHandler={openModalHandler}
			closeModal={closeModal}
			handleExpenseOptionSelect={handleExpenseOptionSelect}
			isLoading={isLoading}
			isFetching={isFetching}
		/>
	);
};

export default TransactionsNewContainer;
